import { useCallback, useMemo, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { flushSync } from 'react-dom'
import { invalidError, notice } from '@/components/pbo/notice'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import Loader from '@/components/content/Loader'
import { canSelectShipper, defaultShipper, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { getShipperListV2 } from '@/services/shipper/apis'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import { useQuery } from '@tanstack/react-query'
import { API_COMMON_CACHE_TIME } from '@/utils/consts'
import { ShipperItem } from '@/services/shipper/types'
import { ALL_INBOUNDS_EXCEL_URL } from '@/services/inbound/allInboundStatus/apis'
import { AllInboundsCondition, allInboundStatusQueryKeys, useGetAllInbounds } from '@/services/inbound/allInboundStatus/useAllInboundStatusQueries'
import { AllInboundItem, GetAllInboundsReq } from '@/services/inbound/allInboundStatus/types'
import {
  DEFAULT_LOGISTICS_SERVICE_TYPE,
  FIELD_NAMES,
  EXCEL_ROW_LIMIT,
  GRID_CDS,
  LOGISTICS_SERVICE_TYPE_OPTIONS,
  MESSAGES,
  ORDER_STATUS_OPTIONS,
  ORDER_TYPE_OPTIONS,
} from './constants'
import { AllInboundStatusFormValues, hasRequiredFilter, toAllInboundsReq, toDetailedReason } from './filterToRequest'
import { AllInboundSearchQuery, fromSearchQuery, toSearchQuery } from './searchQuery'
import { REASON_DETAIL_MAX_LENGTH, REASON_DETAIL_MIN_LENGTH } from './components/ExcelDownloadReasonModal'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'
import InboundInquiryAllPageView, { type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'

/**
 * 화면 기본값 — 화주유형 M1P · 프로필 화주 · 입고예정일자 오늘. 초기화가 되돌릴 기준이다.
 *
 * 화주유형과 화주명은 BE 에서 **AND** 로 걸린다(`buildSearchPredicate` 의 shipperCodes IN + shipper.serviceType IN).
 * 즉 두 칸이 함께 차면 교집합이라, 유형이 세션 화주와 어긋나면 데이터가 있어도 0건이 된다.
 *
 * 여기 M1P 는 **MUSINSA(통합 운영 계정) 기준값**이다. 화주 고정 계정은 이 값을 쓰지 않고
 * 기본 화주의 실제 유형(`defaultServiceType`)으로 덮는다 — 호출부(filterDefaults·onReset) 참조.
 */
const buildDefaultValues = (): AllInboundStatusFormValues => ({
  // BE 는 화주**명** 목록을 받는다(내부에서 코드로 변환) — 세션의 화주명을 기본값으로 채운다.
  shipperNames: AuthStorage.user.get().shipperName ? [String(AuthStorage.user.get().shipperName)] : [],
  logisticsServiceTypes: [DEFAULT_LOGISTICS_SERVICE_TYPE],
  ordOptionNumbers: [],
  // 시작일·종료일 모두 오늘. 조회 범위를 좁혀 첫 진입이 전건 조회가 되지 않게 한다.
  plnDateRange: [dayjs(), dayjs()],
  actDate: null,
  orderType: '',
  orderStatus: '',
})

/** 뷰 옵션 형태로 미리 변환한 상수 — 옵션 슬롯은 렌더마다 불리므로 매번 새 배열을 만들지 않는다(#773 봇). */
const ORDER_TYPE_VIEW_OPTIONS = ORDER_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }))
const ORDER_STATUS_VIEW_OPTIONS = ORDER_STATUS_OPTIONS.map(({ value, label }) => ({ value, label }))

const str = (v: unknown): string => (typeof v === 'string' ? v : '')
const strList = (v: unknown): string[] => (Array.isArray(v) ? (v as unknown[]).map(String).filter(Boolean) : [])
/** 폼의 날짜 범위(DateValue: Dayjs | [from,to] | null) → 뷰 조회영역 값('YYYY-MM-DD' 두 칸). */
const rangeToView = (v: AllInboundStatusFormValues['plnDateRange']): string[] => {
  if (!Array.isArray(v)) return ['', '']
  return [v[0]?.format('YYYY-MM-DD') ?? '', v[1]?.format('YYYY-MM-DD') ?? '']
}
/** 폼의 단일 날짜 → 'YYYY-MM-DD'(없으면 빈 문자열). */
const dateToView = (v: AllInboundStatusFormValues['actDate']): string => (v && !Array.isArray(v) ? v.format('YYYY-MM-DD') : '')
/**
 * 화주유형 목록 — '전체'(빈 문자열 ALL_SERVICE_TYPES_VALUE)를 **그대로 넘긴다**.
 * `toAllInboundsReq` 의 expandAllServiceTypes 가 '전체'를 6종으로 펼친다 — 여기서 벗기면 전개가 무력화돼
 * MUSINSA 가 '전체'만 고르고 화주명 없이 조회할 때 필수 필터에 걸린다(옛 필터는 [''] 를 보존했다).
 */
const typeList = (v: unknown): string[] => (Array.isArray(v) ? (v as unknown[]).map(String) : [])

/**
 * API 행 → 뷰 행. 뷰(컨덕터 생성본)의 표 컬럼 키는 1.0 컬럼명이라 여기서 맞춘다.
 * 데모에만 있는 컬럼(단위코드 ULD_TYPE · 최신입고완료일자)은 2.0 응답에 대응 필드가 없어 빈 칸이다(PR 본문 기록).
 */
const toRow = (item: AllInboundItem): Row => ({
  key: String(item.id),
  service_type: item.serviceType ?? '',
  SPR_NM: item.shipperCode ?? '',
  shipper_name: item.shipperName ?? '',
  ORD_TYPE: item.orderType ?? '',
  BRD_NM: item.brandName ?? '',
  GDS_GROUP1: item.goodsGroup ?? '',
  GDS_CD: item.goodsCode ?? '',
  GDS_NM: item.goodsName ?? '',
  STL_NO: item.styleNumber ?? '',
  GDS_OPT: item.goodsOption ?? '',
  ORD_QTY: item.ordQty,
  BARCODE: item.barcode ?? '',
  ORD_NO: item.ordNumber ?? '',
  ORD_OPT_NO: item.ordOptionNumber ?? '',
  ORD_STATUS: item.orderStatus ?? '',
  PLN_DATE: item.plnDate ?? '',
  ACT_DATE: item.actDate ?? '',
  ACT_QTY: item.actQty,
  TRS_TYPE: item.trsType ?? '',
  TRS_TYPE_DETAIL: item.trsTypeDetail ?? '',
  TRS_LSP_NM: item.trsLspNm ?? '',
  DRV_NAME: item.drvName ?? '',
  TRS_NUMBER: item.trsNumber ?? '',
  TRS_DEPT_DT: item.trsDeptDt ?? '',
  TRS_ARRV_DT: item.trsArrvDt ?? '',
  REF_NO: item.refNo ?? '',
  REF_OPT_NO: item.refOptNo ?? '',
  ORD_PROC_NM: item.ordProcNm ?? '',
  PROC_NM: item.procNm ?? '',
  INS_DATE: item.createdAt ?? '',
  INS_BY: item.createdBy ?? '',
  UPD_DATE: item.updatedAt ?? '',
  UPD_BY: item.updatedBy ?? '',
  LOT_ATTR5: item.lotAttr5 ?? '',
})

/**
 * GR05 입고조회(전체) — 1.0 GR05 를 2.0 API(신규 디자인)로 재구현한 화면. (LOGISPRD-11235)
 *
 * 겉(조회영역·표·엑셀 사유 모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가 그리고,
 * 이 파일은 옛 훅·API·세션/URL 유지·권한 규칙을 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 *
 * 버튼은 3개다 — 조회(SEARCH) · 초기화(CLEAR) · 엑셀 다운로드(툴바 EXPORT). 저장·전송·삭제 없는 **조회 전용 화면**이다.
 *
 * ST11(LOGISPRD-10668)과 다른 지점:
 * - 조회/다운로드가 **POST + 바디**다(ST11 은 GET + 쿼리스트링).
 * - **서버 페이징**이라 페이지 이동마다 재조회한다(ST11 은 전건 수신 후 클라 페이징).
 * - 엑셀 상한이 30,000행이다(ST02/ST07 계열의 100,000 아님).
 * - 엑셀 사유 모달이 있다 — 사유코드·상세사유를 `detailedReason` **쿼리스트링**으로 싣는다.
 *   BE `AccessAuditAspect` 가 요청 **파라미터**를 읽어 EXPORT 감사로그 remark 에 남기므로(LOGISPRD-9602),
 *   POST 바디에 넣으면 읽히지 않는다.
 */
function AllInboundStatusScreen({ coMingle }: Readonly<{ coMingle: boolean }>) {
  // MOMS 에서 화주 계정은 자기 화주만 본다 — 화주유형·화주명이 세션 화주로 고정된다.
  // 통합 운영 화주(FREE_SHIPPERS 4종)만 여러 화주유형/화주명을 자유롭게 고를 수 있다(2.0 공용 규칙, LOGISPRD-14652).
  // 재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고른다 — 함께 고르면 둘 다 조회한다(LOGISPRD-15608, `withCoMingle`).
  const { isFreeShipper, ownShipperName, defaultShipperName, viewerShipperCode, canSelect, selectable } = useMemo(() => {
    const user = AuthStorage.user.get()
    const scope = withCoMingle(shipperScopeOf(user), coMingle)
    const own = user.shipperName ? String(user.shipperName) : ''
    const fallback = defaultShipper(scope)
    return {
      isFreeShipper: !scope.locked,
      ownShipperName: own,
      // 화주명 칸 기본값 — 재고통합 대상 계정은 MUSINSA, 그 밖은 자기 화주(세션 화주명)다 (LOGISPRD-15718)
      defaultShipperName: fallback.shipperCode === scope.shipperCode ? own : fallback.shipperName,
      viewerShipperCode: scope.shipperCode,
      canSelect: canSelectShipper(scope),
      // 이 화면의 화주명 값은 화주**명**이다 — 자기 화주는 세션 화주명(shipperName)으로 바꿔 둔다. 이름이 없는 화주는 고를 수 없다
      selectable: selectableShippers(scope)
        .map((s) => (s.shipperCode === scope.shipperCode ? { shipperCode: s.shipperCode, shipperName: own } : s))
        .filter((s) => !!s.shipperName),
    }
  }, [coMingle])
  const selectableNames = useMemo(() => selectable.map((s) => s.shipperName), [selectable])

  /**
   * 조회 조건 유지 — 조회에 쓴 값을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨,
   * 다른 메뉴 탭을 다녀와도 필터와 목록이 그대로 돌아오게 한다(MOMS 공통 UX — ST09·ST11·MA02 준용).
   */
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<Record<string, unknown>>()
  // 마운트 시 한 번만 복원한다 — 이후 조회로 URL 이 바뀌어도 입력 중인 값을 덮어쓰지 않는다.
  // useMemo 캐시는 React 가 버릴 수 있어 한 번만 계산을 보장하지 않는다 — useState 초기화 함수로 둔다.
  const [restored] = useState(() => fromSearchQuery(parsedQs))

  /**
   * 화주 목록.
   *
   * 통합 운영 화주는 전체를 받고, 화주 고정 계정은 **고를 수 있는 화주만**(보통 자기 화주 하나, 재고통합 ON 이면 MUSINSA 도) 받는다 —
   * 목록을 고르기 위해서가 아니라 **세션 화주의 서비스유형을 알기 위해서**다.
   * 세션(`AuthStorage.user`)에는 화주명·화주코드만 있고 서비스유형이 없다.
   */
  const { data: allShippers = [], isLoading: isShipperListLoading } = useQuery<ShipperItem[]>({
    meta: { errorToast: true }, // 실패는 옛 알럿 대신 새 디자인 알럿(queryClient.ts → notice.failure)
    queryKey: allInboundStatusQueryKeys.shippers(isFreeShipper ? 'ALL' : selectableNames.join(',')),
    queryFn: async (): Promise<ShipperItem[]> => {
      const res = await getShipperListV2({
        shipperNames: isFreeShipper ? [] : selectableNames,
        logisticsServiceTypes: [],
        page: 0,
        size: 10000,
      })
      return res?.data?.items ?? []
    },
    // 화주 고정 계정인데 세션 화주명이 없으면 조회할 것이 없다.
    enabled: isFreeShipper || !!ownShipperName,
    staleTime: API_COMMON_CACHE_TIME,
  })

  /**
   * 세션 화주의 실제 서비스유형(M1P·MFS·MWP…).
   *
   * 화주유형과 화주명은 BE 에서 **AND** 로 걸리므로, 이 값을 M1P 로 못박으면
   * M1P 가 아닌 화주는 자기 데이터가 있어도 0건이 된다. 화주명은 유일하지 않아 **화주코드로 맞춘다**.
   */
  const serviceTypeByName = useMemo(
    () =>
      new Map(
        isFreeShipper ? [] : selectable.map((s) => [s.shipperName, allShippers.find((sh) => sh.shipperCode === s.shipperCode)?.logisticsServiceType || undefined] as const),
      ),
    [isFreeShipper, selectable, allShippers],
  )
  /** 기본 화주의 유형 — 화주유형 기본값은 기본 화주(재고통합 대상이면 MUSINSA)를 따른다 (LOGISPRD-15718) */
  const defaultServiceType = serviceTypeByName.get(defaultShipperName)

  /**
   * 화주 고정 계정의 화주 두 칸 값 — 화주명은 고를 수 있는 화주만 남기고(하나도 없으면 기본 화주), 화주유형은 그 화주들의 유형이다.
   * 두 칸이 BE 에서 AND 라 유형을 자기 화주 것으로 두면 함께 고른 MUSINSA 행이 0건이 된다(LOGISPRD-15608).
   * 고를 화주가 하나뿐인 계정은 늘 자기 화주명·자기 유형이다(종전 그대로).
   */
  const lockedShipperValues = useCallback(
    (names: string[]): Pick<AllInboundStatusFormValues, 'shipperNames' | 'logisticsServiceTypes'> => {
      const kept = names.filter((n) => selectableNames.includes(n))
      const picked = kept.length ? Array.from(new Set(kept)) : defaultShipperName ? [defaultShipperName] : []
      const types = Array.from(new Set(picked.map((n) => serviceTypeByName.get(n)).filter((t): t is string => !!t)))
      return { [FIELD_NAMES.shipperNames]: picked, [FIELD_NAMES.logisticsServiceTypes]: types }
    },
    [selectableNames, defaultShipperName, serviceTypeByName],
  )

  /** 화주명 목록(중복 제거). 통합 운영 화주는 전 화주, 화주 고정 계정은 고를 수 있는 화주. */
  const shipperOptions = useMemo(() => {
    if (!isFreeShipper) return selectableNames.map((n) => ({ value: n, label: n }))
    const seen = new Set<string>()
    return allShippers.reduce<{ value: string; label: string }[]>((acc, s) => {
      const name = s.shipperName || s.shipperCode
      if (!name || seen.has(name)) return acc
      seen.add(name)
      acc.push({ value: name, label: name })
      return acc
    }, [])
  }, [isFreeShipper, allShippers, selectableNames])
  /** 화주 고정 계정의 화주유형 옵션 — 고를 수 있는 화주의 유형. 옵션 슬롯은 렌더마다 불리므로 참조를 고정한다 */
  const lockedServiceTypeOptions = useMemo(
    () => Array.from(new Set(Array.from(serviceTypeByName.values()).filter((t): t is string => !!t)), (t) => ({ value: t, label: t })),
    [serviceTypeByName],
  )

  // 조회 상태 — 전량을 받아 화면이 1만 건씩 나눈다. 페이지 이동은 다시 조회하지 않는다(LOGISPRD-15268).
  /**
   * 복원값이 있으면 그 조건으로 시작한다(RestoredSearch 는 폼 값만 준다 — 요청 형태로 바꾼다).
   * 화주 고정 계정은 URL 값이 고를 수 없는 화주면 세션 화주로 덮는다(옛 initialValues 와 같은 규칙) — 안 덮으면 같은 유형의 **다른 화주** 입고까지 자동조회된다.
   * 필수 필터(화주명 OR 화주유형)가 없으면 복원 대상이 아니다 — 그대로 조회하면 BE 가 400 이라 기본값으로 연다.
   */
  const restoredValues = useMemo(() => {
    if (!restored) return undefined
    return isFreeShipper ? restored.values : { ...restored.values, ...lockedShipperValues(restored.values.shipperNames ?? []) }
  }, [restored, isFreeShipper, lockedShipperValues])
  const restoredCondition = useMemo(() => {
    if (!restoredValues) return undefined
    const condition = toAllInboundsReq(restoredValues)
    return hasRequiredFilter(condition) ? condition : undefined
  }, [restoredValues])
  /**
   * 조회 조건 — null 이면 조회하지 않는다. 복원 조건이 있으면 그 조건으로 켜 둔다: 캐시가 있으면(탭 복귀) 그대로 그리고,
   * 받다 만 캐시면 남은 구간을 이어 받고, 없으면 받는다(공용 훅 staleTime: Infinity — 탭 복귀에 다시 받지 않는다).
   */
  const [searchCondition, setSearchCondition] = useState<AllInboundsCondition | null>(restoredCondition ?? null)
  /** 마지막 조회에 쓴 폼 값 — 엑셀이 같은 조건을 다시 쓴다. */
  const lastSearchValuesRef = useRef<AllInboundStatusFormValues>(restoredValues ?? buildDefaultValues())

  const { rows: items, totalCount, isFirstLoading, isFetchingRest, refetch, guardAction } = useGetAllInbounds(searchCondition)
  const rows = useMemo(() => items.map(toRow), [items])

  // 엑셀 사유코드 — 옛 ExcelDownloadReasonModal 과 같은 공통 콤보(DOWNLOAD_CD).
  const { data: exportCombo } = useGetExportCombo({ errorToast: true })
  const reasonOptions = useMemo(
    () =>
      (exportCombo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: exportCombo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [exportCombo],
  )

  const { isLoading: isDownloading, onDownloadExcel } = useExcelDownload<Omit<GetAllInboundsReq, 'page' | 'size'>>({
    fileNamePrefix: '전체입고',
    config: { method: 'post' },
    errorToast: true,
  })

  /** 조회에 쓴 조건을 메뉴 세션·URL 에 남긴다. 실제 조회와 같은 값이어야 복원이 화면과 일치한다. */
  const persistSearch = useCallback(
    (values: AllInboundStatusFormValues) => {
      const query: AllInboundSearchQuery = toSearchQuery(values)
      MenuStorage.menuOnSession.setSearch(query)
      menuSession.reload()
      replaceQuery(query)
    },
    [menuSession, replaceQuery],
  )

  // ── 뷰 배선(Wiring) — 옛 핸들러의 본문을 슬롯에 잇는다. 조회영역·표·모달·페이저는 뷰가 가진다. ──
  const wiring: Wiring = {
    // 화면관리 그리드 설정(LOGISPRD-17250) — 표 id 는 columnLayout 키와 같다
    permission: usePermissionSlot('gr08', GRID_CDS),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { inqAllTable: coreTableLayoutSlot(GRID_CDS.inqAllTable) },
    rows: {
      // total 없이 주면 뷰가 1만 건씩 페이지를 자르고 목록 검색을 전량에 건다(total 을 주면 서버 페이징 — 생성본 `ROWS_SERVERPAGED`)
      //  — 첫 구간·나머지 구간·재조회 중에는 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390). 받는 만큼 '전체 N'·페이지 수가 늘어나는 게 진행 표시다(gl15 선례).
      ROWS: () => ({ rows, loading: isFirstLoading || isFetchingRest }),
    },
    actions: {
      /** [조회] — 뷰 조회영역 값(키 = filterKey). 화주 고정 계정은 화주 두 칸을 세션 화주로 덮는다. */
      search: (filters) => {
        const plnRange = Array.isArray(filters.inqAllPlnDate) ? (filters.inqAllPlnDate as unknown[]) : []
        const raw: AllInboundStatusFormValues = {
          shipperNames: strList(filters['inqAllShipper.names']),
          logisticsServiceTypes: typeList(filters['inqAllShipper.types']),
          ordOptionNumbers: strList(filters.ORD_OPT_NO),
          plnDateRange: [plnRange[0] ? dayjs(plnRange[0] as string) : null, plnRange[1] ? dayjs(plnRange[1] as string) : null],
          actDate: filters.ACT_DATE ? dayjs(str(filters.ACT_DATE)) : null,
          orderType: str(filters.ORD_TYPE),
          orderStatus: str(filters.ORD_STATUS),
        }
        // 화주 고정 계정(통합 운영 4종 외)은 화주 두 칸을 고를 수 있는 화주로 덮는다 — 어떤 경로로든 화주명이 비면
        // 화주유형만으로 필수 필터를 통과해 같은 유형의 **다른 화주** 입고까지 조회 요청이 나간다.
        const values: AllInboundStatusFormValues = isFreeShipper ? raw : { ...raw, ...lockedShipperValues(raw.shipperNames ?? []) }
        const condition = toAllInboundsReq(values)
        // 화주명 OR 화주유형 — BE validateRequiredFilter 와 같은 조건을 선차단한다.
        if (!hasRequiredFilter(condition)) {
          notice.invalid(MESSAGES.requiredFilter)
          return undefined
        }
        lastSearchValuesRef.current = values
        persistSearch(values)
        // 조건이 커밋되기 전에 refetch 하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다(클릭 문맥이라 안전).
        flushSync(() => setSearchCondition(condition))
        // 같은 조건이어도 서버에 다시 묻는다(1.0) — 키가 같으면 캐시라 켜기만으로는 안 나간다. 조건이 바뀌어 새 키가 스스로 받는 중이면
        //  cancelRefetch: false 로 거기에 합류한다(취소하고 새로 띄우면 취소된 요청도 서버까지 가서 조회가 두 번 나간다).
        // 실패 알럿은 공용 훅이 전역으로 띄운다. 0건 안내는 [조회] 경로에서만(복원 진입에선 부르지 않은 알럿이 된다).
        return refetch({ cancelRefetch: false }).then((res) => {
          if (!res.isError && (res.data?.[0]?.totalCount ?? 0) === 0) notice.invalid(MESSAGES.searchNoRows)
        })
      },
      /** 엑셀 사유 모달 [확인] — 사유는 쿼리스트링으로 싣는다(BE AccessAuditAspect 가 요청 파라미터를 읽어 EXPORT 감사로그에 남긴다, LOGISPRD-9602). */
      inqAllDownloadModal_ok: (ctx) => {
        const reasonCode = str(ctx.form?.reason)
        const reasonDetail = str(ctx.form?.detail).trim()
        // 옛 ExcelDownloadReasonModal 과 같은 검증 — 코드 필수 · 상세 5자 이상 200자 이하.
        const invalid = !reasonCode
          ? '사유코드를 선택해 주세요.'
          : !reasonDetail
            ? '상세사유를 입력해 주세요.'
            : reasonDetail.length < REASON_DETAIL_MIN_LENGTH
              ? `상세사유는 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`
              : reasonDetail.length > REASON_DETAIL_MAX_LENGTH
                ? `상세사유는 ${REASON_DETAIL_MAX_LENGTH}글자 이하로 입력해 주세요.`
                : ''
        if (invalid) {
          return Promise.reject(invalidError(invalid))
        }
        // viewerShipperCode = **보는 사람의 화주**. BE 가 이 값으로 MOMS_GRID_COLUMN_INFO 를 읽어 화주별 엑셀 컬럼을 만든다.
        const params = new URLSearchParams({ detailedReason: toDetailedReason({ reasonCode, reasonDetail }), viewerShipperCode })
        // 화면에 떠 있는 결과와 같은 조건으로 받아야 하므로 폼 현재값이 아니라 마지막 조회 조건을 쓴다.
        return Promise.resolve(onDownloadExcel(`${ALL_INBOUNDS_EXCEL_URL}?${params.toString()}`, searchCondition ?? {})).then(() => undefined)
      },
    },
    options: {
      // 화주 고정 계정은 고를 수 있는 화주의 유형만 고를 수 있다. 유형을 아직 모르면 빈 목록(칸도 비어 있다).
      INQALLSHIPPER_TYPE_OPTIONS: () => (isFreeShipper ? LOGISTICS_SERVICE_TYPE_OPTIONS.map(({ value, label }) => ({ value, label })) : lockedServiceTypeOptions),
      INQALLSHIPPER_NAME_OPTIONS: () => shipperOptions,
      // 입고유형·입고상태는 BE 실측 목록(constants)이 정본 — 데모 목록(출고용 값 포함)이 나가면 조회가 0건이 된다.
      INQALLORDTYPE_OPTIONS: () => ORDER_TYPE_VIEW_OPTIONS,
      INQALLORDSTATUS_OPTIONS: () => ORDER_STATUS_VIEW_OPTIONS,
      // 엑셀 사유코드 — 옛 모달과 같은 공통 콤보(DOWNLOAD_CD).
      REASONRADIO_OPTIONS: () => reasonOptions,
    },
    /** 조회 조건 기본값([초기화] 기준) — 화주유형·화주명·입고예정일자 오늘. 화주 고정 계정은 기본 화주([defaultShipper]) 기준. */
    filterDefaults: {
      inqAllFilterCard: (demo) => {
        const d = buildDefaultValues()
        const types = isFreeShipper ? d.logisticsServiceTypes : defaultServiceType ? [defaultServiceType] : []
        const names = isFreeShipper ? d.shipperNames : defaultShipperName ? [defaultShipperName] : []
        return {
          ...demo,
          'inqAllShipper.types': types,
          'inqAllShipper.names': names,
          inqAllPlnDate: rangeToView(d.plnDateRange),
          ACT_DATE: '',
          ORD_TYPE: '',
          ORD_STATUS: '',
          ORD_OPT_NO: [],
        }
      },
    },
    /**
     * 화주 고정 계정은 화주유형·화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 filterDefaults·search 가 따로 고정한다.
     *  재고통합 ON 의 29CM·INOOINT 는 화주명만 연다 — 화주유형은 [조회] 가 고른 화주의 유형으로 채운다(LOGISPRD-15608)
     */
    filterDisabled: isFreeShipper ? undefined : { service_type: true, shipper_name: !canSelect },
    /** 진입 초기값 — 기본값 위에 URL 복원 조건을 얹는다(마운트 때 한 번). */
    filterInitial: {
      inqAllFilterCard: (defaults) => {
        const v = restoredValues
        if (!v) return defaults
        return {
          ...defaults,
          'inqAllShipper.types': v.logisticsServiceTypes,
          'inqAllShipper.names': v.shipperNames,
          inqAllPlnDate: rangeToView(v.plnDateRange),
          ...(dateToView(v.actDate) ? { ACT_DATE: dateToView(v.actDate) } : {}),
          ...(v.orderType ? { ORD_TYPE: v.orderType } : {}),
          ...(v.orderStatus ? { ORD_STATUS: v.orderStatus } : {}),
          ...(v.ordOptionNumbers?.length ? { ORD_OPT_NO: v.ordOptionNumbers } : {}),
        }
      },
    },
    // 나머지 구간을 받는 동안은 잠그지 않는다 — 받은 행을 보고 넘길 수 있게 두고, 엑셀은 열기 슬롯 첫 줄 guardAction 이 막는다.
    useBusy: () => isFirstLoading || isDownloading,
    opens: {
      /** 엑셀 버튼 — 0건·상한 초과면 사유 모달을 열지 않는다(옛 handleDownload. BE MAX_EXCEL_ROWS 와 같은 기준). */
      inqAllExcelButton: () => {
        // 받는 중·일부 실패면 건수가 서버와 달라 0건·상한 판정이 틀린다(안내는 guardAction 이 띄운다).
        if (!guardAction()) return false
        if (totalCount === 0) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        if (totalCount > EXCEL_ROW_LIMIT) {
          notice.invalid(MESSAGES.excelRowLimitExceeded)
          return false
        }
        return true
      },
    },
    /** [초기화] — 남겨 둔 조회 조건도 함께 지워 다음 진입이 기본값으로 열리게 한다(ST09·ST11 준용). */
    onReset: () => {
      const base = buildDefaultValues()
      const defaults: AllInboundStatusFormValues = isFreeShipper ? base : { ...base, ...lockedShipperValues([]) }
      lastSearchValuesRef.current = defaults
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
      setSearchCondition(null)
    },
  }

  // 화주 고정 계정은 화주 목록이 와야 기본 화주 유형(defaultServiceType)을 안다. 뷰는 filterDefaults 를 마운트 때 한 번만 계산하고 늦게 채우는
  //  슬롯(filterFallback)이 없으므로, 목록이 오기 전에 그리면 잠긴 화주유형 칸이 빈 채로 남는다 — 올 때까지 그리지 않는다(LOGISPRD-14949).
  if (!isFreeShipper && isShipperListLoading) return null
  return <InboundInquiryAllPageView wiring={wiring} />
}

/**
 * 재고통합 FF 첫 조회를 기다린 뒤 화면을 연다 — 화주 범위·URL 복원값은 마운트 첫 렌더에 한 번 만들어지므로
 * FF 값이 정해지기 전에 만들면 잠긴 29CM·INOOINT 의 MUSINSA 복원이 자기 화주로 덮인다(LOGISPRD-15608).
 */
function AllInboundStatus() {
  const { ready, coMingle } = useCoMingleShipperScope()
  if (!ready) return <Loader />
  return <AllInboundStatusScreen coMingle={coMingle} />
}

// 컨덕터 재핸드오프 게이트(문서 32)는 디자인 QA 검증 완료로 제거 — v2 셸 삭제, 이 화면이 곧 진입점(2026-09-11, 성준님 합의).
export default AllInboundStatus
